import React, { useState, useEffect, useCallback } from 'react';
import { budgetApi, projectApi, wbsApi } from '../services/api';
import { Card, Button, Input, Select, Modal, Badge, Spinner } from '../components/UI';
import { Plus, PencilSimple, Trash, CurrencyDollar } from '@phosphor-icons/react';

import { fmtCurrencyShort } from '../utils/currencyFormat';

export default function BudgetsPage() {
  const [budgets, setBudgets] = useState([]);
  const [projects, setProjects] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterProject, setFilterProject] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    project_id: '', activity_id: '', material_budget: '', labor_budget: '', other_budget: ''
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([projectApi.list(), wbsApi.listActivities()])
      .then(([p, a]) => { setProjects(p.data); setActivities(a.data); })
      .finally(() => setLoading(false));
  }, []);

  const loadBudgets = useCallback(async () => {
    const res = await budgetApi.list(filterProject || undefined);
    setBudgets(res.data);
  }, [filterProject]);

  useEffect(() => { loadBudgets(); }, [loadBudgets]);

  const fmt = (n) => fmtCurrencyShort(n);

  const openCreate = () => {
    setEditing(null);
    setForm({ project_id: filterProject || '', activity_id: '', material_budget: '', labor_budget: '', other_budget: '' });
    setError(''); setShowModal(true);
  };

  const openEdit = (b) => {
    setEditing(b);
    setForm({
      project_id: b.project_id, activity_id: b.activity_id,
      material_budget: b.material_budget, labor_budget: b.labor_budget, other_budget: b.other_budget
    });
    setError(''); setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setError(''); setSaving(true);
    try {
      const data = {
        ...form,
        material_budget: parseFloat(form.material_budget) || 0,
        labor_budget: parseFloat(form.labor_budget) || 0,
        other_budget: parseFloat(form.other_budget) || 0
      };
      if (editing) await budgetApi.update(editing.id, data);
      else await budgetApi.create(data);
      setShowModal(false); loadBudgets();
    } catch (err) { setError(err.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this budget?')) return;
    try { await budgetApi.delete(id); loadBudgets(); }
    catch (err) { alert(err.response?.data?.detail || 'Failed to delete'); }
  };

  const totalBudget = budgets.reduce((s, b) => s + (b.total_budget || 0), 0);

  if (loading) return <div className="flex justify-center py-8"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-4" data-testid="budgets-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#0A0A0A]">Activity Budgets</h1>
          <p className="text-sm text-[#666]">Set material, labor & other budgets per project activity</p>
        </div>
        <Button onClick={openCreate} data-testid="add-budget-btn"><Plus className="w-4 h-4 mr-2" /> New Budget</Button>
      </div>

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <Select value={filterProject} onChange={(e) => setFilterProject(e.target.value)} className="w-64" data-testid="filter-project">
          <option value="">All Projects</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </Select>
        {budgets.length > 0 && (
          <div className="bg-[#002FA7]/10 text-[#002FA7] px-3 py-1.5 rounded-lg text-sm font-bold" data-testid="total-budget">
            Total: {fmt(totalBudget)}
          </div>
        )}
      </div>

      {budgets.length === 0 ? (
        <Card className="p-8 text-center text-[#666]">No budgets set yet</Card>
      ) : (
        <div className="space-y-2">
          {budgets.map(b => (
            <Card key={b.id} className="p-3 sm:p-4" data-testid={`budget-row-${b.id}`}>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <CurrencyDollar className="w-4 h-4 text-[#002FA7]" />
                    <span className="font-semibold text-sm text-[#0A0A0A]">{b.activity_name}</span>
                    <span className="text-xs text-[#666]">{b.project_name}</span>
                  </div>
                  <div className="flex flex-wrap gap-4 mt-1.5 text-xs text-[#666]">
                    <span>Material: <b className="text-[#0A0A0A]">{fmt(b.material_budget)}</b></span>
                    <span>Labor: <b className="text-[#0A0A0A]">{fmt(b.labor_budget)}</b></span>
                    <span>Other: <b className="text-[#0A0A0A]">{fmt(b.other_budget)}</b></span>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <div className="bg-[#002FA7]/10 text-[#002FA7] px-2 py-1 rounded text-sm font-bold">{fmt(b.total_budget)}</div>
                  <button onClick={() => openEdit(b)} className="p-1 text-[#666] hover:text-[#002FA7]" data-testid={`edit-budget-${b.id}`}><PencilSimple className="w-4 h-4" /></button>
                  <button onClick={() => handleDelete(b.id)} className="p-1 text-[#666] hover:text-[#FF3333]" data-testid={`delete-budget-${b.id}`}><Trash className="w-4 h-4" /></button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editing ? 'Edit Budget' : 'Set Budget'}>
        <form onSubmit={handleSubmit} className="space-y-3" data-testid="budget-form">
          {error && <div className="p-2 bg-[#FF3333]/10 text-[#FF3333] rounded text-sm">{error}</div>}
          <Select label="Project" value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })} required disabled={!!editing} data-testid="budget-project-select">
            <option value="">-- Select --</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </Select>
          <Select label="Activity" value={form.activity_id} onChange={(e) => setForm({ ...form, activity_id: e.target.value })} required disabled={!!editing} data-testid="budget-activity-select">
            <option value="">-- Select --</option>
            {activities.map(a => <option key={a.id} value={a.id}>{a.trade} / {a.activity_name}</option>)}
          </Select>
          <Input label="Material Budget ($)" type="number" min="0" step="any" value={form.material_budget} onChange={(e) => setForm({ ...form, material_budget: e.target.value })} data-testid="budget-material-input" />
          <Input label="Labor Budget ($)" type="number" min="0" step="any" value={form.labor_budget} onChange={(e) => setForm({ ...form, labor_budget: e.target.value })} data-testid="budget-labor-input" />
          <Input label="Other Budget ($)" type="number" min="0" step="any" value={form.other_budget} onChange={(e) => setForm({ ...form, other_budget: e.target.value })} data-testid="budget-other-input" />
          <div className="text-right text-sm font-semibold text-[#002FA7]" data-testid="budget-total-preview">
            Total: {fmt((parseFloat(form.material_budget) || 0) + (parseFloat(form.labor_budget) || 0) + (parseFloat(form.other_budget) || 0))}
          </div>
          <div className="flex justify-end space-x-3 pt-3">
            <Button type="button" variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="save-budget-btn">{saving ? 'Saving...' : 'Save'}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
